import type {
  LoadedScript,
  ScriptCapabilities,
  ScriptCapabilityApi,
  ScriptHook,
  ScriptHost,
  ScriptInvocationContext,
  ScriptLanguage,
  ScriptLimits,
} from "@roqeet/core";
import { DEFAULT_SCRIPT_LIMITS } from "@roqeet/core";
// 타입만 import(빌드 시 erase) — 실제 모듈은 lazy import로 가져온다(결정 #6: Lua는
// 카드가 실제로 Lua를 담을 때만 로드). 이렇게 하면 초기 번들에서 Lua WASM이 빠진다.
import type { LuaEngine, LuaFactory as LuaFactoryT } from "wasmoon";
// glue.wasm을 Vite 에셋으로 번들해 그 URL을 LuaFactory에 넘긴다. 기본값은 unpkg CDN을
// 받아오는데(브라우저에서 실패 + 로컬 우선 #1 위배), 번들 URL을 쓰면 오프라인에서도
// 동작하고 CDN 의존이 사라진다. ?url은 문자열만 주므로 wasmoon JS는 여전히 lazy-load다.
import glueWasmUrl from "wasmoon/dist/glue.wasm?url";

/**
 * Lua 스크립트 호스트 — 결정 #6(Risu 호환) / #4·#5·#17(권한 기반 샌드박스).
 *
 * RisuAI 카드의 `triggerlua`/Lua 트리거를 실행하기 위한 런타임이다. wasmoon
 * (Lua 5.4 → WASM)을 lazy-load하며, QuickJS JS 호스트와 **동일한 capability 계약**을
 * 따른다(같은 ScriptCapabilityApi 주입, 같은 한도).
 *
 * 보안(결정 #17):
 *  - 표준 라이브러리를 통째로 열지 않는다(openStandardLibs: false). io/os/package/
 *    debug처럼 파일·프로세스·동적 로딩이 가능한 라이브러리는 **로드하지 않는다**.
 *    base/table/string/math/utf8/coroutine 같은 순수 계산 라이브러리만 연다.
 *  - base에서 동적 코드 로딩 경로(load/loadstring/dofile/loadfile/require/collectgarbage)를
 *    nil로 덮어 게스트가 임의 바이트코드/파일을 끌어오지 못하게 한다.
 *  - 메모리 캡 + 함수 타임아웃(명령 카운트 훅)으로 6s+ 기기를 보호한다.
 *
 * 1차 버전은 safe 등급(getVar/setVar/getChatMessages/setResult/log/random/now)을
 * 완전 지원하고, network 권한이 있으면 elevated 중개 fetch도 노출한다(QuickJS와 동일).
 */
let factoryPromise: Promise<LuaFactoryT> | null = null;

/** wasmoon LuaFactory를 lazy-load(최초 1회). */
async function getFactory(): Promise<LuaFactoryT> {
  if (!factoryPromise) {
    // 환경변수를 ASCII로 고정한다. emscripten은 `_` env를 실행 경로(node에선
    // process.argv[1])에서 채우는데, 경로에 비ASCII 문자가 있으면 WASI environ_get이
    // assertion(charCode>255)으로 죽는다. 브라우저에선 무해하지만 명시 고정이 안전.
    factoryPromise = import("wasmoon").then(
      (m) => {
        const g = globalThis as Record<string, unknown>;
        const isNode = typeof g["process"] === "object" && (g["process"] as Record<string, unknown>)?.["release"] != null;
        const wasmUrl = isNode ? undefined : glueWasmUrl;
        return new m.LuaFactory(wasmUrl, { _: "roqeet", PWD: "/", HOME: "/" });
      }
    );
  }
  return factoryPromise;
}

/** 게스트에서 제거할 위험/동적-로딩 base 전역(결정 #17). */
const STRIPPED_GLOBALS = [
  "load",
  "loadstring",
  "loadfile",
  "dofile",
  "require",
  "collectgarbage",
  "io",
  "os",
  "package",
  "debug",
  "newproxy",
];

export class LuaScriptHost implements ScriptHost {
  private readonly limits: ScriptLimits;

  constructor(limits: ScriptLimits = DEFAULT_SCRIPT_LIMITS) {
    this.limits = limits;
  }

  supports(language: ScriptLanguage): boolean {
    return language === "lua";
  }

  async load(
    def: {
      id: string;
      language: ScriptLanguage;
      source: string;
      hooks: ScriptHook[];
      capabilities: ScriptCapabilities;
      consentGranted: boolean;
    },
    api: ScriptCapabilityApi,
  ): Promise<LoadedScript> {
    if (def.language !== "lua") {
      throw new Error(`LuaScriptHost는 lua만 지원합니다(받은 값: ${def.language}).`);
    }
    const factory = await getFactory();
    const limits = this.limits;

    return {
      id: def.id,
      language: def.language,
      hooks: def.hooks,

      async invoke(
        hook: ScriptHook,
        context: ScriptInvocationContext,
      ): Promise<ScriptInvocationContext> {
        // 안전 샌드박스 엔진: 표준 라이브러리 전체 개방 금지. 함수 타임아웃으로
        // 무한 루프 차단(명령 카운트 훅). 매 invoke마다 새 엔진(상태 누수 차단).
        // traceAllocations: setMemoryMax(메모리 캡, 결정 #4/#17 DoS 방어)에 필요.
        const engine: LuaEngine = await factory.createEngine({
          openStandardLibs: false,
          injectObjects: false,
          enableProxy: false,
          traceAllocations: true,
          functionTimeout: limits.timeoutMs,
        });
        engine.global.setMemoryMax(limits.memoryBytes);

        let resultText = context.text ?? "";
        let resultSet = false;

        try {
          // 안전 라이브러리만 선택적으로 로드(io/os/package/debug 제외).
          const { LuaLibraries } = await import("wasmoon");
          engine.global.loadLibrary(LuaLibraries.Base);
          engine.global.loadLibrary(LuaLibraries.Table);
          engine.global.loadLibrary(LuaLibraries.String);
          engine.global.loadLibrary(LuaLibraries.Math);
          engine.global.loadLibrary(LuaLibraries.Coroutine);
          engine.global.loadLibrary(LuaLibraries.UTF8);

          // 동적 로딩/위험 전역 제거(결정 #17).
          for (const name of STRIPPED_GLOBALS) {
            engine.global.set(name, undefined);
          }

          injectLuaApi(engine, api, hook, context, {
            setResult: (t: string) => {
              resultText = t;
              resultSet = true;
            },
          });

          await engine.doString(def.source);

          return resultSet ? { ...context, text: resultText } : context;
        } catch (e) {
          // 스크립트 실패는 격리 — 호스트/대화를 죽이지 않고 원본 유지(결정 #4).
          console.error("Lua 스크립트 실행 오류", def.id, e);
          return context;
        } finally {
          engine.global.close();
        }
      },

      dispose() {
        // 엔진은 invoke마다 생성·해제하므로 여기선 할 일 없음.
      },
    };
  }

  dispose(): void {
    // 팩토리는 프로세스 수명 동안 캐시(재사용). 명시적 해제 불필요.
  }
}

/**
 * 게스트(Lua)에 capability API를 주입한다(결정 #17). QuickJS JS 호스트와 동일한
 * 표면을 노출하되, 자료형은 Lua 관례를 따른다:
 *  - context는 테이블 `{ hook, text }`.
 *  - getChatMessages는 1-인덱스 배열 테이블(각 항목 `{ role, content, name? }`).
 *  - 권한 없는 기능은 주입하지 않아 게스트에서 도달 불가능.
 */
function injectLuaApi(
  engine: LuaEngine,
  api: ScriptCapabilityApi,
  hook: ScriptHook,
  context: ScriptInvocationContext,
  hostHooks: { setResult: (t: string) => void },
): void {
  const g = engine.global;

  // 읽기 전용 스냅샷 컨텍스트(plain object → Lua 테이블로 변환).
  g.set("context", { hook, text: context.text ?? "" });

  g.set("setResult", (t: unknown) => {
    hostHooks.setResult(typeof t === "string" ? t : String(t ?? ""));
  });

  g.set("log", (...args: unknown[]) => {
    console.log("[lua]", ...args);
  });

  g.set("random", () => api.random());
  g.set("now", () => api.now());

  g.set("getVar", (scope: unknown, key: unknown) => {
    const v = api.getVar(String(scope) as "chat" | "character" | "global", String(key));
    if (v == null) return undefined;
    return typeof v === "string" ? v : JSON.stringify(v);
  });

  g.set("setVar", (scope: unknown, key: unknown, value: unknown) => {
    api.setVar(String(scope) as "chat" | "character" | "global", String(key), value);
  });

  g.set("getChatMessages", (limit?: unknown) => {
    const n = typeof limit === "number" ? limit : undefined;
    // wasmoon이 JS 배열/객체를 Lua 테이블로 변환(enableProxy:false라 깊은 복사).
    return api.getChatMessages(n);
  });

  // 중개 fetch(elevated, 결정 #17): network 권한 + 동의가 있을 때만 주입.
  // wasmoon은 Promise를 반환하는 JS 함수를 Lua promise userdata로 전달한다. Lua
  // 게스트는 코루틴 스레드에서 `fetch(url, init):await()`로 값을 받는다:
  //   local res = fetch("https://api.example.com/x"):await()
  //   -- res = { ok = bool, status = number, body = string }
  if (typeof api.fetch === "function") {
    const brokeredFetch = api.fetch;
    g.set("fetch", async (url: unknown, init?: unknown) => {
      const initObj =
        init && typeof init === "object"
          ? (init as { method?: string; headers?: Record<string, string>; body?: string })
          : undefined;
      try {
        const res = await brokeredFetch(String(url), initObj);
        return { ok: res.ok, status: res.status, body: await res.text() };
      } catch (e) {
        return { ok: false, status: 0, body: e instanceof Error ? e.message : String(e) };
      }
    });
  }

  // 스토리지(elevated, 결정 #17): storage 권한 + 동의가 있을 때만 주입. fetch와
  // 동일하게 Promise userdata로 전달되므로 게스트는 `:await()`로 값을 받는다:
  //   local raw = storageGet("key"):await()   -- JSON 문자열 또는 nil
  //   storageSet("key", value):await()        -- 값은 JSON 직렬화되어 영속
  // 값은 JSON 문자열로 오간다(QuickJS 경로와 동일 표면).
  if (typeof api.storageGet === "function") {
    const get = api.storageGet;
    g.set("storageGet", async (key: unknown) => {
      try {
        const v = await get(String(key));
        return v === undefined ? undefined : JSON.stringify(v);
      } catch (e) {
        console.error("Lua storageGet 오류", e);
        return undefined;
      }
    });
  }
  if (typeof api.storageSet === "function") {
    const set = api.storageSet;
    g.set("storageSet", async (key: unknown, value: unknown) => {
      try {
        await set(String(key), value);
      } catch (e) {
        console.error("Lua storageSet 오류", e);
      }
      return undefined;
    });
  }
}
